import * as React from "react";
import clsx from "clsx";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import { Callout } from "../../../ui/components/Callout";
import { Checkbox } from "../../../ui/components/Checkbox";
import { Switch } from "../../../ui/components/Switch";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import { useOrganizationContextOptional } from "../../lib/organization-context";
import {
  commitSmartLinkProductFullSync,
  formatCategoryCounts,
  fetchSmartLinkProductEnvironments,
  previewSmartLinkProductFullSync,
} from "../../lib/api/smart-link";
import type {
  SmartLinkImportEnvironment,
  SmartLinkProductCommitResult,
  SmartLinkProductPreviewItem,
} from "../../lib/api/smart-link";
import * as s from "./SmartLinkFullSyncModal.css";

export interface SmartLinkProductFullSyncModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onCommitted: () => void;
}

type Step = "pick-environment" | "reviewing" | "done";

function isBlocked(item: SmartLinkProductPreviewItem, overrides: Record<string, string>): boolean {
  if (item.conflicts.length > 0) return true;
  if (item.existingProProduct) return false;
  return item.model.kind !== "resolved" && !overrides[item.smartProductId];
}

export function SmartLinkProductFullSyncModal({ open, setOpen, onCommitted }: SmartLinkProductFullSyncModalProps) {
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const orgContext = useOrganizationContextOptional();
  const token = accessToken ?? (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);
  const organizations = orgContext?.availableOrgs ?? [];

  const [environments, setEnvironments] = React.useState<SmartLinkImportEnvironment[]>([]);
  const [environment, setEnvironment] = React.useState<"primary" | "alt">("primary");
  const [organizationId, setOrganizationId] = React.useState<string>("");
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const [step, setStep] = React.useState<Step>("pick-environment");
  const [loadingPreview, setLoadingPreview] = React.useState(false);
  const [items, setItems] = React.useState<SmartLinkProductPreviewItem[]>([]);
  const [unsupported, setUnsupported] = React.useState("");
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [overrides, setOverrides] = React.useState<Record<string, string>>({});
  const [error, setError] = React.useState<string | null>(null);
  const [committing, setCommitting] = React.useState(false);
  const [result, setResult] = React.useState<SmartLinkProductCommitResult | null>(null);

  React.useEffect(() => {
    if (!open) {
      setStep("pick-environment");
      setItems([]);
      setSelectedIds(new Set());
      setOverrides({});
      setError(null);
      setResult(null);
      return;
    }
    setOrganizationId(orgContext?.selectedOrgIds?.[0] ?? "");
    if (!token) return;
    fetchSmartLinkProductEnvironments(token)
      .then((envs) => {
        setEnvironments(envs);
        if (envs[0]) setEnvironment(envs[0].id);
      })
      .catch(() => setEnvironments([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token]);

  const visibleItems = React.useMemo(
    () => items.filter((i) => includeArchived || !i.archived),
    [items, includeArchived],
  );
  const selectable = visibleItems.filter((i) => !isBlocked(i, overrides));
  const selectedCount = [...selectedIds].filter((id) => selectable.some((i) => i.smartProductId === id)).length;

  const handleLookup = async () => {
    if (!token) return;
    setLoadingPreview(true);
    setError(null);
    try {
      const preview = await previewSmartLinkProductFullSync(token, environment);
      setItems(preview.items);
      setUnsupported(formatCategoryCounts(preview.unsupported));
      setSelectedIds(
        new Set(preview.items.filter((i) => !i.archived && !isBlocked(i, {})).map((i) => i.smartProductId)),
      );
      setStep("reviewing");
    } catch (err: any) {
      const errorCode = err?.data?.error ?? "";
      if (errorCode === "smart_link_api_not_configured") {
        setError(t("pro.system.smartLink.import.error.notConfigured"));
      } else if (errorCode === "smart_link_api_unavailable" || err?.status === 503) {
        setError(t("pro.system.smartLink.import.error.unavailable"));
      } else {
        setError(err?.message || t("pro.system.smartLink.products.fullSync.error.lookup"));
      }
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleCommit = async () => {
    if (!token) return;
    setCommitting(true);
    setError(null);
    try {
      const ids = selectable.filter((i) => selectedIds.has(i.smartProductId)).map((i) => i.smartProductId);
      const res = await commitSmartLinkProductFullSync(token, {
        environment,
        organizationId: organizationId || null,
        smartProductIds: ids,
        modelOverrides: Object.fromEntries(Object.entries(overrides).filter(([id]) => ids.includes(id))),
        includeArchived,
      });
      setResult(res);
      setStep("done");
      onCommitted();
    } catch (err: any) {
      setError(err?.message || t("pro.system.smartLink.products.fullSync.error.commit"));
    } finally {
      setCommitting(false);
    }
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(selectable.map((i) => i.smartProductId)) : new Set());
  };

  const toggleOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const setOverride = (id: string, modelId: string) => {
    setOverrides((prev) => ({ ...prev, [id]: modelId }));
    if (modelId) toggleOne(id, true);
  };

  function rowNote(item: SmartLinkProductPreviewItem) {
    if (item.conflicts.length > 0) {
      return (
        <span className={s.matchBadge} title={item.conflicts.map((c) => `${c.field}: ${c.value} (${c.productName})`).join("\n")}>
          {t("pro.system.smartLink.products.fullSync.conflict")}
        </span>
      );
    }
    if (item.existingProProduct) {
      return (
        <span className={s.matchBadge} title={t("pro.system.smartLink.products.fullSync.existingTooltip", { name: item.existingProProduct.name })}>
          {t("pro.system.smartLink.products.fullSync.existing")}
        </span>
      );
    }
    if (item.model.kind === "ambiguous") {
      return (
        <select
          className={clsx(formStyles.input, s.rowSelect)}
          value={overrides[item.smartProductId] ?? ""}
          onChange={(e) => setOverride(item.smartProductId, e.target.value)}
          aria-label={t("pro.system.smartLink.products.fullSync.pickModel")}
        >
          <option value="">{t("pro.system.smartLink.products.fullSync.pickModel")}</option>
          {item.model.candidates.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      );
    }
    if (item.model.kind === "unmapped") {
      return (
        <span className={s.matchBadge}>
          {t(`pro.system.smartLink.products.fullSync.unmapped.${item.model.reason}`)}
        </span>
      );
    }
    return null;
  }

  return (
    <Modal open={open} setOpen={setOpen} minWidth="560px" maxWidth="760px" maxHeight="80vh" closeOnClickOff={!committing}>
      <Modal.Header>
        <Text level="h2" weight="bold" className={s.title}>
          {t("pro.system.smartLink.products.fullSync.title")}
        </Text>
        <p className={s.subtitle}>{t("pro.system.smartLink.products.fullSync.subtitle")}</p>
      </Modal.Header>

      <Modal.Body>
        {step === "pick-environment" && (
          <div className={s.fields}>
            <div className={s.field}>
              <label className={formStyles.label}>{t("pro.system.smartLink.import.environment")}</label>
              <select
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as "primary" | "alt")}
                disabled={loadingPreview || environments.length === 0}
                className={formStyles.input}
              >
                {environments.length === 0 && (
                  <option value="primary">{t("pro.system.smartLink.import.noEnvironments")}</option>
                )}
                {environments.map((env) => (
                  <option key={env.id} value={env.id}>
                    {env.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={s.field}>
              <label className={formStyles.label}>{t("pro.system.smartLink.products.fullSync.organization")}</label>
              <select value={organizationId} onChange={(e) => setOrganizationId(e.target.value)} className={formStyles.input}>
                <option value="">{t("pro.system.smartLink.products.fullSync.noOrganization")}</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {step === "reviewing" && unsupported && (
          <div className={s.errorWrap}>
            <Callout variant="info">
              {t("pro.system.smartLink.products.fullSync.unsupported", { categories: unsupported })}
            </Callout>
          </div>
        )}

        {step === "reviewing" &&
          (visibleItems.length === 0 ? (
            <Callout variant="info">{t("pro.system.smartLink.products.fullSync.nothingToImport")}</Callout>
          ) : (
            <>
              <div className={s.reviewHeader}>
                <Checkbox
                  checked={selectable.length > 0 && selectedCount === selectable.length}
                  indeterminate={selectedCount > 0 && selectedCount < selectable.length}
                  onCheckedChange={toggleAll}
                  aria-label={t("pro.system.smartLink.fullSync.selectAll")}
                />
                <Text level="body" weight="medium">
                  {t("pro.system.smartLink.fullSync.selectedCount", { selected: selectedCount, total: visibleItems.length })}
                </Text>
                <span className={s.headerAside}>
                  <Switch
                    checked={includeArchived}
                    onCheckedChange={setIncludeArchived}
                    label={t("pro.system.smartLink.products.fullSync.includeArchived")}
                  />
                </span>
              </div>
              <div className={s.rowList}>
                {visibleItems.map((item) => {
                  const blocked = isBlocked(item, overrides);
                  return (
                    <div key={item.smartProductId} className={s.row}>
                      <Checkbox
                        checked={!blocked && selectedIds.has(item.smartProductId)}
                        disabled={blocked}
                        onCheckedChange={(checked) => toggleOne(item.smartProductId, checked)}
                        aria-label={item.name ?? item.smartProductId}
                      />
                      <div className={s.rowInfo}>
                        <p className={s.rowEmail}>{item.name || item.smartProductId}</p>
                        <p className={s.rowName}>
                          {[item.categoryType, item.serial, item.archived ? t("pro.system.smartLink.products.fullSync.archived") : null]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </p>
                      </div>
                      {rowNote(item)}
                    </div>
                  );
                })}
              </div>
            </>
          ))}

        {step === "done" && result && (
          <>
            <Callout variant="success">
              {t("pro.system.smartLink.products.fullSync.doneMessage", {
                created: result.created,
                linked: result.linked,
                skipped: result.skipped.length,
              })}
            </Callout>
            {result.skipped.length > 0 && (
              <div className={s.rowList}>
                {Object.entries(
                  result.skipped.reduce<Record<string, typeof result.skipped>>((groups, skip) => {
                    (groups[skip.reason] ??= []).push(skip);
                    return groups;
                  }, {}),
                ).map(([reason, skips]) => (
                  <div key={reason} className={s.skipGroup}>
                    <p className={s.rowEmail}>
                      {t(`pro.system.smartLink.products.fullSync.skip.${reason}`)} ({skips.length})
                    </p>
                    {skips.map((skip) => (
                      <p key={skip.smartProductId} className={s.skipDetail} title={skip.detail ?? undefined}>
                        {skip.smartProductId}
                        {skip.detail ? ` — ${skip.detail}` : ""}
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {error && (
          <div className={s.errorWrap}>
            <Callout variant="error">{error}</Callout>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {step === "pick-environment" && (
          <>
            <Button type="button" color="neutral" onClick={() => setOpen(false)}>
              {t("pro.system.smartLink.import.cancel")}
            </Button>
            <Button
              type="button"
              color="primary"
              loading={loadingPreview}
              disabled={loadingPreview || environments.length === 0}
              onClick={() => void handleLookup()}
            >
              {t("pro.system.smartLink.products.fullSync.lookup")}
            </Button>
          </>
        )}
        {step === "reviewing" && (
          <>
            <Button type="button" color="neutral" onClick={() => setStep("pick-environment")} disabled={committing}>
              {t("pro.system.smartLink.fullSync.back")}
            </Button>
            {visibleItems.length > 0 && (
              <Button
                type="button"
                color="primary"
                loading={committing}
                disabled={committing || selectedCount === 0}
                onClick={() => void handleCommit()}
              >
                {t("pro.system.smartLink.products.fullSync.confirmImport", { count: selectedCount })}
              </Button>
            )}
          </>
        )}
        {step === "done" && (
          <Button type="button" color="primary" onClick={() => setOpen(false)}>
            {t("shared.common.close")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
